/**
 * S10, the table list. "Your data", at a glance.
 *
 * THE SCREEN HAS ONE JOB and it is not browsing: it is making it obvious that
 * the database is real, is theirs, and that they did not have to set it up.
 * Everything on the page is arranged around that sentence. The grounding line
 * says the connection out loud, the list is a ledger of things their app made
 * rather than a gallery of cards, and the export control is visible without
 * being reached for, because a cloud that hides the way out is a cloud a
 * person cannot leave.
 *
 * THE EMPTY STATE IS THE MOST IMPORTANT STATE ON THIS SCREEN, which is not
 * true of any other list in the product. A brand new project has no tables
 * because nothing has written to it yet, and the honest answer to "how do I
 * make one" is that the person's coding agent does it, in their code, not in
 * this console. So the empty state hands over a prompt instead of a button.
 * That is the whole teaching moment, and it is why Soi Cats is seeded with a
 * connected database and zero tables.
 *
 * SYSTEM TABLES ARE HIDDEN IN SIMPLE, and that is the one place this screen
 * subtracts, the only one, and it does not extend to anything else here. It is
 * not a violation of the Advanced rule: `_sv_migrations` is not the person's
 * table, it is ours, and a row a person cannot act on and did not create is
 * furniture in Simple and a fact in Advanced. The export menu used to claim
 * the same exception for its second item, printing "SQL file" in Advanced and
 * "Backup file" in Simple. That was a replacement, not a subtraction, and it
 * is gone: one label, both modes.
 *
 * STATES, all deep linkable: ?state=empty, ?state=export-menu,
 * ?state=exporting, ?state=loading.
 *
 * // reads through environmentOf(); do not flatten.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { showToast } from '../../components/primitives/Toast';
import { NarratedLine } from '../../components/patterns/AsyncNarration';
import { Notice } from '../../components/patterns/Notice';
import { EmptyState } from '../../components/patterns/EmptyState';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { Skeleton } from '../../components/patterns/Skeleton';
import { UsageBar } from '../../components/patterns/UsageBar';
import { whenLabelOrNull } from '../../components/patterns/DeployRow';

import { getProject, getUsage, listRowsPage, listTables } from '../../lib/api';
import { useProjects } from '../../lib/auth/use-app-data';
import { toCsvBook, toSqlDump, downloadText } from '../../lib/export';
import type { ExportTable } from '../../lib/export';
import { count } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { useProjectSlug } from '../../lib/hooks/use-project-slug';
import { t } from '../../lib/i18n/t';
import { DATABASE_AGENT_PROMPT } from '../../lib/mock/seed';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { DbTable } from '../../lib/types';

import '../../styles/screens/database.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

/** How long the export narrates before the file arrives. Speed scaled. */
export const EXPORT_MS = 900;

/**
 * cloud-api's ROW_CAP (services/database-read.ts), repeated here only to
 * name the number in the sentence a person reads when their export was cut
 * short. It is not used to page or slice anything: the server decides how
 * much it hands over and tells us whether it held any back.
 */
export const EXPORT_ROW_CAP = 200;

type ExportKind = 'csv' | 'sql';

/** Ours, not theirs. Hidden in Simple, shown and marked in Advanced. */
export function isSystemTable(name: string): boolean {
  return name.startsWith('_sv_');
}

function Chevron() {
  return (
    <span className="trow__go" aria-hidden="true">
      <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
        <path
          d="M6 3.5 10.5 8 6 12.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function Lock() {
  return (
    <span className="trow__lock" aria-hidden="true">
      <svg viewBox="0 0 16 16" width="11" height="11" focusable="false">
        <rect x="3.5" y="7" width="9" height="6.5" rx="1.2" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <path d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7" fill="none" stroke="currentColor" strokeWidth="1.3" />
      </svg>
    </span>
  );
}

export default function Database() {
  const slug = useProjectSlug() ?? '';
  const { search } = useLocation();
  const forced = new URLSearchParams(search).get('state');

  const [advanced] = useAdvanced();
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const speed = useDemoStore((s) => s.prefs.speed);

  const [menuOpen, setMenuOpen] = useState(forced === 'export-menu');
  const [exporting, setExporting] = useState<ExportKind | null>(
    forced === 'exporting' ? 'csv' : null,
  );
  /** Tables the last export could only take the first page of. */
  const [truncatedTables, setTruncatedTables] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const cachedProjects = useProjects();
  const cachedProject = slug ? cachedProjects.find((p) => p.slug === slug) : undefined;
  const { data: fetchedProject, isLoading: isProjectLoading, error: projectError } = useAsync(
    () => (slug ? getProject(slug) : Promise.resolve(null)),
    [slug, scenarioId],
  );
  const project = fetchedProject ?? cachedProject;
  const projectId = project?.id;

  const { data: tablesData, error: tablesError } = useAsync(
    () => (projectId ? listTables(projectId) : Promise.resolve(undefined)),
    [projectId, scenarioId],
  );
  const { data: usage } = useAsync(
    () => (projectId ? getUsage(projectId) : Promise.resolve(null)),
    [projectId, scenarioId],
  );

  useEffect(() => {
    if (!menuOpen) return;
    function onDown(event: MouseEvent) {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const tables = useMemo<DbTable[]>(() => {
    const all = tablesData ?? [];
    if (forced === 'empty') return [];
    return advanced ? all : all.filter((one) => !isSystemTable(one.name));
  }, [tablesData, advanced, forced]);

  useEffect(() => {
    if (project) {
      captureEvent('database_tables_viewed', {
        project_slug: project.slug,
        table_count: tables.length,
      });
    }
  }, [project, tables.length]);

  if ((isProjectLoading && !project) || forced === 'loading') {
    return (
      <div className="page">
        <Header slug={slug} advanced={advanced} onExport={() => undefined} busy={null} />
        <Skeleton shape="row" count={5} label={t('Loading your tables')} />
      </div>
    );
  }

  if (projectError || tablesError) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Database')}</h1>
        </header>
        <EmptyState
          mark="table"
          sentence={t("We couldn't load your tables. Try refreshing the page.")}
        />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Database')}</h1>
        </header>
        <EmptyState
          mark="table"
          sentence={t('We cannot find that project.')}
          action={
            <Button variant="outline" onClick={() => window.history.back()}>
              {t('Go back')}
            </Button>
          }
        />
      </div>
    );
  }

  const base = `/p/${encodeURIComponent(project.slug)}`;
  const storage = usage?.metrics.find((metric) => metric.resource === 'database-storage');

  async function runExport(kind: ExportKind) {
    captureEvent('database_export_clicked', {
      project_slug: project?.slug,
      format: kind,
    });
    setMenuOpen(false);
    setExporting(kind);

    const ms = speed === 'instant' ? 0 : EXPORT_MS / speed;
    await new Promise((resolve) => window.setTimeout(resolve, ms));

    const source = tablesData ?? [];
    const pages = projectId
      ? await Promise.all(
          source.map(async (one) => {
            const page = await listRowsPage(projectId, one.name);
            return { name: one.name, columns: one.columns, rows: page.rows, truncated: page.truncated };
          }),
        )
      : [];
    const loaded: ExportTable[] = pages.map(({ name, columns, rows }) => ({ name, columns, rows }));

    // A capped export is still worth having, and saying so is the whole
    // difference between a partial backup and a lie. The read behind this
    // is the same capped one the grid uses (cloud-api's ROW_CAP), so a big
    // table exports its first page and this names which tables were cut.
    // The real fix is a streamed, server-side export, which cannot be a
    // browser download: see apps/cloud/PLANNING.md F23.
    const cut = pages.filter((one) => one.truncated).map((one) => one.name);

    const stem = project?.slug ?? 'database';
    if (kind === 'csv') {
      downloadText(`${stem}-tables.csv`, toCsvBook(loaded), 'text/csv');
      showToast(t('Your export is ready.'), `${stem}-tables.csv`);
    } else {
      downloadText(`${stem}.sql`, toSqlDump(loaded), 'application/sql');
      showToast(t('Your export is ready.'), `${stem}.sql`);
    }
    setTruncatedTables(cut);
    captureEvent('database_export_downloaded', {
      project_slug: project?.slug,
      format: kind,
      table_count: loaded.length,
      truncated_table_count: cut.length,
    });
    setExporting(null);
  }

  return (
    <div className="page">
      <div className="db__top">
        <Header
          slug={slug}
          advanced={advanced}
          busy={exporting}
          onExport={() => setMenuOpen((was) => !was)}
        />

        {menuOpen ? (
          <div className="db__menu" ref={menuRef} role="menu" aria-label={t('Export')}>
            <button
              type="button"
              className="db__menu-item"
              role="menuitem"
              onClick={() => void runExport('csv')}
            >
              <span className="db__menu-name">{t('CSV file')}</span>
              <span className="db__menu-note">
                {t('Every table in one file. Open it in a spreadsheet.')}
              </span>
            </button>
            <button
              type="button"
              className="db__menu-item"
              role="menuitem"
              onClick={() => void runExport('sql')}
            >
              {/* One label, in both modes. Advanced adds detail, it never
                  renames a thing the product already has a word for, and a
                  menu item that reads differently depending on a switch is the
                  one shape a person cannot learn. The note underneath already
                  says exactly what the file is. */}
              <span className="db__menu-name">{t('Backup file')}</span>
              <span className="db__menu-note">
                {t('Everything in your database, in one file another database can read.')}
              </span>
            </button>
          </div>
        ) : null}
      </div>

      {/* The one thing allowed to move on this screen, and only while it runs. */}
      {exporting ? (
        <div className="db__narrating">
          <NarratedLine sentence={t('Preparing your export')} />
        </div>
      ) : null}

      {/* Never a toast. "Your backup is missing rows" is a fact about the
          file that was just saved, and a message that leaves on its own is
          a message somebody restores from a partial backup without ever
          having read. */}
      {truncatedTables.length > 0 ? (
        <Notice
          tone="attention"
          title={t('Your export does not have everything in it.')}
          actions={
            <Button variant="outline" onClick={() => setTruncatedTables([])}>
              {t('Got it')}
            </Button>
          }
        >
          <p>
            {t(
              'We can only read the first {n} rows of a table at a time right now, so these tables were cut short: {tables}.',
              { n: count(EXPORT_ROW_CAP), tables: truncatedTables.join(', ') },
            )}
          </p>
          <p>{t('We are building a full export that can handle any size.')}</p>
        </Notice>
      ) : null}

      {tables.length === 0 ? (
        <EmptyState
          mark="table"
          sentence={t('Tables will appear here when your app creates them.')}
          context={t('Your coding agent writes them in your code. This console does not.')}
          agentPrompt={DATABASE_AGENT_PROMPT}
        />
      ) : (
        <ul className="tlist">
          {tables.map((one) => {
            const system = isSystemTable(one.name);
            return (
              <li className="tlist__item" key={one.name}>
                <Link
                  className="trow"
                  to={`${base}/database/${encodeURIComponent(one.name)}`}
                  data-system={system ? 'yes' : 'no'}
                >
                  <span className="trow__name">
                    {system ? <Lock /> : null}
                    {one.name}
                  </span>
                  {system ? (
                    <span className="trow__chip">{t('Managed by SV Cloud')}</span>
                  ) : null}
                  <span className="trow__rows mono">
                    {one.rowCount === 1
                      ? t('1 row')
                      : t('{n} rows', { n: count(one.rowCount) })}
                  </span>
                  <span className="trow__when">
                    {(() => {
                      const when = whenLabelOrNull(one.updatedAt);
                      return when ? t('changed {when}', { when }) : null;
                    })()}
                  </span>
                  <Chevron />
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {storage ? (
        <SectionPanel
          title={t('Usage')}
          action={
            <Link className="db__more" to={`${base}/usage`}>
              {t('See usage')}
            </Link>
          }
        >
          <UsageBar
            label={t('Database')}
            used={storage.used}
            limit={storage.limit}
            unit={storage.unit}
          />
        </SectionPanel>
      ) : null}


    </div>
  );
}

/* ---------------------------------------------------------------- header */

function Header({
  slug,
  advanced,
  busy,
  onExport,
}: {
  slug: string;
  advanced: boolean;
  busy: ExportKind | null;
  onExport: () => void;
}) {
  const navigate = useNavigate();

  return (
    <div className="db__head">
      <header className="page__head">
        <h1 className="page__title">{t('Database')}</h1>
        <p className="page__lede">
          {t("Your app's data lives here. Your app is connected to it automatically.")}
        </p>
      </header>

      <div className="db__actions">
        {advanced && USE_MOCK ? (
          <Button
            variant="outline"
            onClick={() => navigate(`/p/${encodeURIComponent(slug)}/database/query`)}
          >
            {t('Run a query')}
          </Button>
        ) : null}
        <Button variant="outline" onClick={onExport} disabled={busy !== null}>
          {t('Export')}
        </Button>
      </div>
    </div>
  );
}
